// Composer Studio — 사진 업로드(widget:'photo') 필드의 **발견사항 조치사진 반영**(findingWriteBack) 설정.
//
// 서버는 문서가 **결재 완료(APPROVED)** 로 전이할 때 이 선언이 있는 소스 행의 사진을 점검 발견사항 조치사진으로
// 쓴다(`reports/utils/finding-resolution-writeback.util` — 결정 2026-09-14 「개선 후 사진의 발견사항 반영」, 같은 날
// 「제출 시 → 결재 완료 시」 재결정: 승인된 문서만 바깥 사실을 만든다). 그 조건(편집형 조립
// 목록 소스 · photo 위젯 · 발견사항 id 필드)은 shared `judgeFindingWriteBackField` **한 함수**를 서버
// 수집기와 여기가 같이 쓴다 — 조건 목록을 두 벌로 손미러하면 갈라지는 날 UI가 「반영됩니다」라 거짓
// 안내한다(2026-09-14 은닉 감사 3차 `rowScopeWriteBack` 선례). 미충족이면 체크박스를 잠그고 사유를
// 보인다. **이미 켜진 값은 미충족이어도 끌 수 있다**(트랩 방지 — WriteBackSelector 규약 동형).
//
// 현장/조직 저장소 역전파(`writeBack` — 다음 보고서 재사용)와 **다른 축**이다: 그쪽은 저장 시 정본
// 재사용, 이쪽은 확정 시 점검 조치 상태 반영. 같은 패널에 나란히 두되 라벨로 가른다.
//
// 규약 정본: shared `FindingWriteBackConfig` JSDoc.

import {
  FINDING_WRITE_BACK_ELIGIBLE_NOTE,
  findingWriteBackIdField,
  isFindingWriteBackDeclared,
  judgeFindingWriteBackField,
  type DataSourceDef,
  type DataSourceFieldMap,
  type FindingWriteBackConfig,
} from '@safetyhub/shared';
import { patchDataSourceField } from './dataSourceWriteForm';

/** 체크박스 접근성 이름 — spec·호출부가 같은 문자열을 쓴다. */
export const FINDING_WRITE_BACK_TOGGLE_LABEL = '결재 완료 시 발견사항 조치사진 반영';

/** 켜진 선언의 규칙 요지 — shared 안내 문장 + 서버 반영 규칙(조치자·재해소). */
export const FINDING_WRITE_BACK_ON_NOTE = `${FINDING_WRITE_BACK_ELIGIBLE_NOTE}. 조치자=문서 작성자, 이미 해소된 발견사항은 사진만 바뀌고 해소 시각은 유지됩니다`;

export interface FindingWriteBackFieldSettingsProps {
  /** 이 필드가 속한 선언 — 판정(소스 형상·id 필드)의 근거. */
  def: DataSourceDef;
  /** dataSources 전체 배열 기준 인덱스(불변 갱신 좌표). */
  defIndex: number;
  field: DataSourceFieldMap;
  fieldIndex: number;
  dataSources: DataSourceDef[];
  /** 필드가 비편집(editable:false)이면 값 보존한 채 시각만 잠근다(위젯 키 칸과 동일 규약). */
  disabled?: boolean;
  onChange: (next: DataSourceDef[]) => void;
}

export function FindingWriteBackFieldSettings({
  def,
  defIndex,
  field,
  fieldIndex,
  dataSources,
  disabled = false,
  onChange,
}: FindingWriteBackFieldSettingsProps) {
  const judged = judgeFindingWriteBackField({ def, field });
  const declared = isFindingWriteBackDeclared(field);
  const ineligible = judged.kind === 'ineligible';
  // 미충족이면 켜지 못하게 잠그되, 이미 켜진 값은 끌 수 있어야 한다(트랩 방지).
  const locked = ineligible && !declared;
  const idField = findingWriteBackIdField(field.findingWriteBack);
  // id 필드 후보는 **발견사항 id를 실제로 실을 수 있는 필드**로 좁힌다 — 카탈로그 경로가 `findingId`로
  // 끝나거나(from: 'primary.findingId'), 그 이름 자체인 필드. 아무 필드(no·locationText…)를 고르면
  // 서버 수집기(`report-composer.finding-writeback.ts`)가 `id in (…)` 0건으로 **조용히 0**을 돌려주는데
  // 안내는 「등록됩니다」라 거짓 안내가 된다(7차 은닉 감사 B-). 저장값이 후보 밖이면 아래 보존 옵션이 남는다.
  const idCandidates = def.fields.filter(
    (f) =>
      f.field !== field.field &&
      (f.field === 'findingId' || /(^|\.)findingId$/.test(String(f.from ?? ''))),
  );

  const patch = (next: FindingWriteBackConfig | undefined) =>
    onChange(patchDataSourceField(dataSources, defIndex, fieldIndex, { findingWriteBack: next }));

  return (
    <div>
      <label className="flex items-center gap-1.5 text-xs text-gray-600">
        <input
          type="checkbox"
          aria-label={FINDING_WRITE_BACK_TOGGLE_LABEL}
          checked={declared}
          disabled={disabled || locked}
          onChange={(e) =>
            patch(
              e.target.checked
                ? { kind: 'resolution-photo', when: 'approved', ...(field.findingWriteBack?.findingIdField ? { findingIdField: field.findingWriteBack.findingIdField } : {}) }
                : undefined,
            )
          }
        />
        {FINDING_WRITE_BACK_TOGGLE_LABEL}
      </label>
      <p
        className={`mt-1 text-[10px] leading-snug ${ineligible && declared ? 'text-amber-600' : 'text-gray-400'}`}
      >
        {ineligible
          ? declared
            ? `${judged.reason} — 이 설정은 결재 완료 시 아무것도 반영하지 않으니 끄세요`
            : judged.reason
          : declared
            ? FINDING_WRITE_BACK_ON_NOTE
            : `켜면 ${FINDING_WRITE_BACK_ELIGIBLE_NOTE}`}
      </p>
      {declared && !ineligible && (
        <div className="mt-1.5">
          <select
            value={idField}
            disabled={disabled}
            aria-label="발견사항 id 필드"
            onChange={(e) =>
              patch({
                kind: 'resolution-photo',
                when: 'approved',
                // 기본 이름(findingId)을 고르면 키를 지운다 — 저장 JSON에 기본값을 박지 않는다.
                ...(e.target.value && e.target.value !== 'findingId' ? { findingIdField: e.target.value } : {}),
              })
            }
            className="w-full min-w-0 text-sm border border-gray-300 rounded px-1.5 py-1 bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-brand-400"
            title="같은 행에서 점검 발견사항 id를 읽을 필드 — 기본 findingId"
          >
            {/* 저장값이 목록 밖이면(필드가 삭제·개명된 옛 매핑) 보존 옵션으로 남긴다 — 서명자 이름 필드 규약 동형. */}
            {!idCandidates.some((c) => c.field === idField) ? (
              <option value={idField}>{idField} (필드 선언 밖 — 보존)</option>
            ) : null}
            {idCandidates.map((c) => (
              <option key={c.field} value={c.field}>
                {c.label ? `${c.label} (${c.field})` : c.field}
              </option>
            ))}
          </select>
          <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
            발견사항 id 필드 — 행이 어느 점검 발견사항인지 가리키는 값(보통 hidden 'findingId')
          </p>
        </div>
      )}
      <p className="mt-1 text-[10px] leading-snug text-gray-400">
        현장/조직 저장소 역전파(다음 보고서 재사용)와 다른 축 — 점검의 조치 상태에 씁니다. 초안 저장·결재 제출·
        문서에서의 사진 삭제는 점검에 반영되지 않습니다(반려된 문서는 흔적을 남기지 않고, 조치 취소는 점검 상세에서).
      </p>
    </div>
  );
}

export default FindingWriteBackFieldSettings;
